import React, { useEffect, useState, useRef } from 'react';
import Layout from '../components/Layout.jsx';
import './Profile.css';
import { profileService } from '../services/apiService';
import { getProfilePhotoUrl } from '../utils/imageUtils.js';
import { useAuth } from '../context/AuthContext.jsx';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [social] = useState({});
  const { user } = useAuth();
  const employee_code = localStorage.getItem('employee_code');
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const [imageVersion, setImageVersion] = useState(null);
  const imgRef = useRef(null);

  // Function to get profile image with fallback (load from backend)
  const getProfileImage = () => {
    if (profile?.profile_photo) {
      const base = getProfilePhotoUrl(profile.profile_photo);
      return imageVersion ? `${base}?v=${imageVersion}` : base;
    }
    return `${API_BASE_URL}/uploads/user.png`;
  };

  useEffect(() => {
    async function fetchProfile() {
      try {
        const data = await profileService.getProfile();
        setProfile(data.data || data); // adjust as per your API response
        setForm(data.data?.employee || data || {});
        // reset image version on fresh load (no cache-bust)
        setImageVersion(null);
        try { localStorage.setItem('profileImageVersion', ''); } catch (e) {}
      } catch {
        setProfile(null);
      }
    }
    fetchProfile();
    // If you want to keep social links, you can fetch them here as before
  }, [employee_code]);

  return (
    <Layout>
      <main className="profile-page">
        <header className="profile-header">
          <h1>My Profile</h1>
          <p>Personal & professional information</p>
          {/* <div style={{marginTop:8}}>
            <a href="/change-password" className="btn btn-link">Change password</a>
          </div> */}
        </header>

        <section className="profile-container">
          {/* Left Profile Card */}
          <aside className="profile-card">
            {/* Profile Photo with Pencil Icon (All Employees) */}
            <div style={{ position: 'relative', display: 'inline-block', width: 140, height: 140, margin: '0 auto' }}>
              <img
                src={getProfileImage()}
                ref={imgRef}
                alt="Profile"
                className="profile-avatar"
                onError={(e) => {
                  e.target.src = `${API_BASE_URL}/uploads/user.png`;
                }}
              />
              {user && (
                <>
                  <button
                    onClick={() => document.getElementById('photoInput').click()}
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: '#0d6efd',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: 18,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                    }}
                    title="Edit profile photo"
                  >
                    <i className="bi bi-pencil"></i>
                  </button>
                  <input
                    id="photoInput"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const id = profile?.employee_id || user?.employee_id;
                      const fd = new FormData();
                      fd.append('profile_photo', file);
                      try {
                        setUploading(true);
                        await profileService.updateProfilePhoto(id, fd);
                        const refreshed = await profileService.getProfile();
                          setProfile(refreshed.data || refreshed);
                          // bump image version so browser reloads new image
                          const ver = Date.now();
                          setImageVersion(ver);
                          try { localStorage.setItem('profileImageVersion', ver.toString()); } catch (e) {}
                          // Force reload the img element to bypass any stubborn cache
                          setTimeout(() => {
                            try {
                              if (imgRef.current) {
                                imgRef.current.src = getProfileImage();
                              }
                            } catch (e) {}
                          }, 120);
                          // clear the file input so same file can be re-selected later
                          try { e.target.value = ''; } catch (e) {}
                        setUploading(false);
                          // Update global userInfo in localStorage so header can reflect new photo
                          try {
                            const stored = localStorage.getItem('userInfo');
                            if (stored) {
                              const ui = JSON.parse(stored);
                              ui.profile_photo = refreshed.data?.employee?.profile_photo || refreshed.data?.profile_photo || ui.profile_photo;
                              localStorage.setItem('userInfo', JSON.stringify(ui));
                              // notify other parts of the app (Header/AuthContext) about update
                              window.dispatchEvent(new CustomEvent('profileUpdated', { detail: { profile_photo: ui.profile_photo } }));
                            }
                          } catch (e) {}
                      } catch (err) {
                        setUploading(false);
                        console.error('Upload failed', err);
                        alert(err?.response?.data?.message || err.message || 'Upload failed');
                      }
                    }}
                  />
                </>
              )}
            </div>
            {uploading && <p style={{ textAlign: 'center', color: '#666', fontSize: 12, marginTop: 8 }}>Uploading...</p>}

            <h2>{profile?.first_name} {profile?.last_name}</h2>
            <span className="profile-role">
              {profile?.designation || profile?.role}
            </span>

            <div className="profile-socials">
              <a href={social.twitter || '#'} className={!social.twitter ? 'disabled' : ''} target="_blank" rel="noreferrer">
                <i className="bi bi-twitter"></i>
              </a>
              <a href={social.facebook || '#'} className={!social.facebook ? 'disabled' : ''} target="_blank" rel="noreferrer">
                <i className="bi bi-facebook"></i>
              </a>
              <a href={social.instagram || '#'} className={!social.instagram ? 'disabled' : ''} target="_blank" rel="noreferrer">
                <i className="bi bi-instagram"></i>
              </a>
              <a href={social.linkedin || '#'} className={!social.linkedin ? 'disabled' : ''} target="_blank" rel="noreferrer">
                <i className="bi bi-linkedin"></i>
              </a>
            </div>
          </aside>

          {/* Right Details Section */}
          <section className="profile-details">
            {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h3>Profile Details</h3>
              {user && (
                <button 
                  className="btn btn-primary" 
                  onClick={() => setIsEditing(!isEditing)}
                  style={{ padding: '0.5rem 1rem' }}
                >
                  {isEditing ? 'Cancel' : <><i className="bi bi-pencil"></i> Edit</> }
                </button>
              )}
            </div> */}

            {/* Edit Form (Shown only when editing) */}
            {isEditing && (
              <div className="edit-form" style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                <h4 style={{ marginTop: 0 }}>Edit Profile Information</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div className="form-row">
                    <label>First Name</label>
                    <input 
                      value={form.first_name || ''} 
                      onChange={(e)=>setForm({...form, first_name: e.target.value})} 
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div className="form-row">
                    <label>Last Name</label>
                    <input 
                      value={form.last_name || ''} 
                      onChange={(e)=>setForm({...form, last_name: e.target.value})}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div className="form-row">
                    <label>Contact</label>
                    <input 
                      value={form.Contact_Number || ''} 
                      onChange={(e)=>setForm({...form, Contact_Number: e.target.value})}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div className="form-row">
                    <label>Designation</label>
                    <input 
                      value={form.Designation || form.designation || ''} 
                      onChange={(e)=>setForm({...form, Designation: e.target.value})}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button 
                    className="btn btn-success" 
                    onClick={async()=>{
                      try{
                        const payload = {...form};
                        const id = profile?.employee_id || user?.employee_id;
                        await profileService.updateProfile(id, payload);
                        const refreshed = await profileService.getProfile();
                        setProfile(refreshed.data || refreshed);
                        setForm(refreshed.data || refreshed);
                        setIsEditing(false);
                      }catch(err){
                        console.error('Update failed', err);
                        alert(err?.response?.data?.message || err.message || 'Update failed');
                      }
                    }}
                    style={{ padding: '0.5rem 1rem' }}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            )}

            {/* Read-Only Details Grid */}
            <div className="details-grid">
              <div className="detail-item">
                <span>Employee ID</span>
                <p>{profile?.employee_code}</p>
              </div>

              <div className="detail-item">
                <span>Full Name</span>
                <p>{profile?.first_name} {profile?.last_name}</p>
              </div>

              <div className="detail-item">
                <span>Email</span>
                <p>{profile?.email_id}</p>
              </div>

              <div className="detail-item">
                <span>Contact Number</span>
                <p>{profile?.Contact_Number || '-'}</p>
              </div>

              <div className="detail-item">
                <span>Designation</span>
                <p>{profile?.designation || profile?.role}</p>
              </div>

              <div className="detail-item">
                <span>Joining Date</span>
                <p>
                  {profile?.Joiningdate
                    ? new Date(profile.Joiningdate).toLocaleDateString()
                    : '-'}
                </p>
              </div>
            </div>
          </section>
        </section>
      </main>
    </Layout>
  );
};

export default Profile;